import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import cl from './Style.module.css';
import AdminService from '../../API/AdminService';
import logger from '../../utils/logger';
import {
    IconArrowLeft, IconEvents, IconSearch, IconChevronUp, IconChevronDown,
    IconClose, IconAlert, IconTrash,
} from '../../components/Icons';

// SVG-стрелки сортировки — заменяют unicode ↕↑↓
function SortIcon({ active, dir }) {
    if (!active) {
        return (
            <span style={{
                display: 'inline-flex', flexDirection: 'column', marginLeft: 4,
                color: 'var(--sk-content-text-muted)', opacity: 0.6,
            }}>
                <IconChevronUp width={9} height={9}/>
                <IconChevronDown width={9} height={9} style={{ marginTop: -2 }}/>
            </span>
        );
    }
    return (
        <span style={{ display: 'inline-flex', marginLeft: 4, color: '#b08620' }}>
            {dir === 'asc' ? <IconChevronUp width={12} height={12}/> : <IconChevronDown width={12} height={12}/>}
        </span>
    );
}

const EditEvent = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Состояние сортировки
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Модальные окна
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Дни недели для выпадающего списка (с опцией "Несколько")
    const eventDaysOfWeek = [
        { value: '', label: 'Не выбрано' },
        { value: 1, label: 'Понедельник' },
        { value: 2, label: 'Вторник' },
        { value: 3, label: 'Среда' },
        { value: 4, label: 'Четверг' },
        { value: 5, label: 'Пятница' },
        { value: 6, label: 'Суббота' },
        { value: 0, label: 'Воскресенье' },
        { value: -1, label: 'Несколько' }
    ];

    // Статусы для выпадающего списка
    const statusOptions = [
        { value: 'Активный', label: 'Активный' },
        { value: 'Завершённый', label: 'Завершённый' }
    ];

    const [editedEvent, setEditedEvent] = useState({
        name: '',
        day: '',
        timeStart: '',
        timeEnd: '',
        dateStart: '',
        dateEnd: '',
        status: ''
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    useEffect(() => {
        filterEvents();
    }, [events, searchTerm]);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            const response = await AdminService.getAllEvents();
            setEvents(response.data || []);
        } catch (error) {
            logger.error('Ошибка загрузки мероприятий:', error);
            setError('Ошибка загрузки мероприятий');
        } finally {
            setLoading(false);
        }
    };

    const filterEvents = () => {
        if (!searchTerm) {
            setFilteredEvents(events);
            return;
        }

        const filtered = events.filter(event => {
            const searchLower = searchTerm.toLowerCase();
            return (
                (event.name || '').toLowerCase().includes(searchLower) ||
                (event.status || '').toLowerCase().includes(searchLower) ||
                (event.timeStart || '').toLowerCase().includes(searchLower) ||
                (event.timeEnd || '').toLowerCase().includes(searchLower) ||
                (event.dateStart || '').toLowerCase().includes(searchLower) ||
                (event.dateEnd || '').toLowerCase().includes(searchLower) ||
                getDayName(event.day).toLowerCase().includes(searchLower)
            );
        });
        setFilteredEvents(filtered);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        const sortedEvents = [...filteredEvents].sort((a, b) => {
            let aValue = a[key];
            let bValue = b[key];

            if (key === 'day') {
                aValue = getDayName(aValue);
                bValue = getDayName(bValue);
            }

            if (key === 'time') {
                aValue = a.timeStart || '';
                bValue = b.timeStart || '';
            }

            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            if (direction === 'asc') {
                return aValue.toString().localeCompare(bValue.toString(), 'ru', { numeric: true });
            } else {
                return bValue.toString().localeCompare(aValue.toString(), 'ru', { numeric: true });
            }
        });

        setFilteredEvents(sortedEvents);
    };

    const getSortIcon = (columnKey) => (
        <SortIcon active={sortConfig.key === columnKey} dir={sortConfig.direction}/>
    );

    const handleRowClick = (event) => {
        setSelectedEvent(event);
        setEditedEvent({
            name: event.name || '',
            day: event.day ?? '',
            timeStart: event.timeStart || '',
            timeEnd: event.timeEnd || '',
            dateStart: event.dateStart || '',
            dateEnd: event.dateEnd || '',
            status: event.status || 'Активный'
        });
        setShowEditModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedEvent(prev => ({
            ...prev,
            [name]: name === 'day' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const handleSave = async () => {
        if (!editedEvent.name) {
            showNotification('Название мероприятия обязательно для заполнения', 'error');
            return;
        }

        setIsSaving(true);
        try {
            await AdminService.updateEvent(selectedEvent.id, editedEvent);
            showNotification('Мероприятие успешно обновлено!', 'success');
            setShowEditModal(false);
            fetchEvents();
        } catch (error) {
            logger.error('Ошибка обновления мероприятия:', error);
            showNotification('Ошибка обновления мероприятия', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteClick = () => {
        setShowEditModal(false);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);
        try {
            await AdminService.deleteEvent(selectedEvent.id);
            showNotification('Мероприятие успешно удалено!', 'success');
            setShowDeleteModal(false);
            fetchEvents();
        } catch (error) {
            logger.error('Ошибка удаления мероприятия:', error);
            showNotification('Ошибка удаления мероприятия', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const showNotification = (message, type) => {
        setNotification({ message, type });
        setTimeout(() => {
            setNotification('');
        }, 3000);
    };

    const getDayName = (dayNumber) => {
        const day = eventDaysOfWeek.find(d => d.value === dayNumber);
        return day ? day.label : (dayNumber !== null && dayNumber !== undefined && dayNumber !== '' ? dayNumber : 'Не выбрано');
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loading}>Загрузка мероприятий...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.error}>{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                <button onClick={() => navigate('/main/jornal/settings')} className={cl.backLink}>
                    <IconArrowLeft width={14} height={14}/>
                    <span>К настройкам</span>
                </button>

                <div className={cl.pageHeader}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconEvents width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Редактирование мероприятий</h1>
                                <span className={cl.pageHeaderPill}>
                                    <span className={cl.pageHeaderPillStrong}>{filteredEvents.length}</span>
                                    <span className={cl.pageHeaderPillMuted}>
                                        {filteredEvents.length === 1 ? 'мероприятие' : 'мероприятий'}
                                    </span>
                                </span>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Настройки · Мероприятия
                            </div>
                        </div>
                    </div>
                </div>

                {/* Уведомления */}
                {notification && (
                    <div className={notification.type === 'success' ? cl.notification : cl.error}>
                        {notification.message}
                    </div>
                )}

                {/* Поле поиска */}
                <div className={cl.searchContainer}>
                    <span className={cl.searchIcon}>
                        <IconSearch width={18} height={18}/>
                    </span>
                    <input
                        type="text"
                        placeholder="Поиск по всем полям…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={cl.searchInput}
                    />
                </div>

                {/* Таблица мероприятий */}
                <div className={cl.tableContainer}>
                    {filteredEvents.length === 0 ? (
                        <div className={cl.noData}>
                            {searchTerm ? 'Мероприятия не найдены по запросу' : 'Мероприятия не найдены'}
                        </div>
                    ) : (
                        <table className={cl.studentsTable}>
                            <thead>
                                <tr>
                                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                        Название{getSortIcon('name')}
                                    </th>
                                    <th onClick={() => handleSort('day')} style={{ cursor: 'pointer' }}>
                                        День недели{getSortIcon('day')}
                                    </th>
                                    <th onClick={() => handleSort('time')} style={{ cursor: 'pointer' }}>
                                        Время{getSortIcon('time')}
                                    </th>
                                    <th onClick={() => handleSort('dateStart')} style={{ cursor: 'pointer' }}>
                                        Дата начала{getSortIcon('dateStart')}
                                    </th>
                                    <th onClick={() => handleSort('dateEnd')} style={{ cursor: 'pointer' }}>
                                        Дата окончания{getSortIcon('dateEnd')}
                                    </th>
                                    <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                        Статус{getSortIcon('status')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredEvents.map((event) => (
                                    <tr
                                        key={event.id}
                                        className={cl.studentRow}
                                        onClick={() => handleRowClick(event)}
                                    >
                                        <td data-label="Название">
                                            <div className={cl.studentName}>
                                                {event.name}
                                            </div>
                                        </td>
                                        <td data-label="День">{getDayName(event.day)}</td>
                                        <td data-label="Время">
                                            {event.timeStart && event.timeEnd
                                                ? `${event.timeStart} - ${event.timeEnd}`
                                                : event.timeStart || event.timeEnd || '-'
                                            }
                                        </td>
                                        <td data-label="Начало">{event.dateStart || '-'}</td>
                                        <td data-label="Окончание">{event.dateEnd || '-'}</td>
                                        <td data-label="Статус">
                                            <span className={event.status === 'Завершённый' ? cl.statusArchive : cl.statusActive}>
                                                {event.status || 'Активный'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Модальное окно редактирования */}
            {showEditModal && selectedEvent && (
                <ModalForm
                    title="Редактировать мероприятие"
                    icon={<IconEvents width={18} height={18}/>}
                    onClose={() => setShowEditModal(false)}
                >
                    <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className={cl.modalForm}>
                        <div className={cl.modalFormRow}>
                            <Field label="Название мероприятия *">
                                <input
                                    type="text"
                                    name="name"
                                    value={editedEvent.name}
                                    onChange={handleInputChange}
                                    className={cl.modalInput}
                                    required
                                />
                            </Field>
                            <Field label="День недели">
                                <select
                                    name="day"
                                    value={editedEvent.day}
                                    onChange={handleInputChange}
                                    className={cl.modalInput}
                                >
                                    {eventDaysOfWeek.map(day => (
                                        <option key={day.value} value={day.value}>
                                            {day.label}
                                        </option>
                                    ))}
                                </select>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Время начала">
                                <input type="time" name="timeStart" value={editedEvent.timeStart}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Время окончания">
                                <input type="time" name="timeEnd" value={editedEvent.timeEnd}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <div className={cl.modalFormRow}>
                            <Field label="Дата начала">
                                <input type="date" name="dateStart" value={editedEvent.dateStart}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                            <Field label="Дата окончания">
                                <input type="date" name="dateEnd" value={editedEvent.dateEnd}
                                    onChange={handleInputChange} className={cl.modalInput}/>
                            </Field>
                        </div>

                        <Field label="Статус мероприятия">
                            <select
                                name="status"
                                value={editedEvent.status}
                                onChange={handleInputChange}
                                className={cl.modalInput}
                            >
                                {statusOptions.map(status => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </select>
                        </Field>

                        <div className={cl.modalActionsSplit}>
                            <button
                                type="button"
                                onClick={handleDeleteClick}
                                className={cl.btnDanger}
                            >
                                <IconTrash width={14} height={14}/>
                                <span>Удалить мероприятие</span>
                            </button>
                            <div className={cl.modalActionsRight}>
                                <button
                                    type="button"
                                    onClick={() => setShowEditModal(false)}
                                    className={cl.btnSecondary}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className={cl.btnPrimary}
                                >
                                    {isSaving ? 'Сохранение…' : 'Сохранить'}
                                </button>
                            </div>
                        </div>
                    </form>
                </ModalForm>
            )}

            {/* Модальное окно подтверждения удаления */}
            {showDeleteModal && (
                <ModalForm
                    title="Удалить мероприятие?"
                    icon={<IconAlert width={18} height={18}/>}
                    onClose={() => setShowDeleteModal(false)}
                    variant="danger"
                >
                    <div className={cl.confirmBody}>
                        <p className={cl.confirmText}>
                            Вы точно хотите удалить мероприятие <strong>«{selectedEvent?.name}»</strong>?
                        </p>
                        <p className={cl.confirmHint}>
                            Восстановить его будет нельзя — все данные, связанные с мероприятием, сотрутся.
                        </p>
                    </div>
                    <div className={cl.modalActions}>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className={cl.btnSecondary}
                        >
                            Отмена
                        </button>
                        <button
                            onClick={handleDeleteConfirm}
                            disabled={isDeleting}
                            className={cl.btnDangerSolid}
                        >
                            {isDeleting ? 'Удаление…' : 'Да, удалить'}
                        </button>
                    </div>
                </ModalForm>
            )}
        </div>
    );
};


// Универсальная модалка-форма (тёмный overlay, светлая карточка, иконка в заголовке)
function ModalForm({ title, icon, onClose, variant, children }) {
    const iconClass = variant === 'danger'
        ? `${cl.modalCardIcon} ${cl.modalCardIconDanger}`
        : cl.modalCardIcon;
    return (
        <div className={cl.modalOverlayV2} onClick={onClose}>
            <div className={cl.modalCardV2} onClick={e => e.stopPropagation()}>
                <header className={cl.modalCardHead}>
                    <div className={cl.modalCardTitleWrap}>
                        <span className={iconClass}>{icon}</span>
                        <h2 className={cl.modalCardTitle}>{title}</h2>
                    </div>
                    <button className={cl.modalCardClose} onClick={onClose} aria-label="Закрыть">
                        <IconClose width={18} height={18}/>
                    </button>
                </header>
                <div className={cl.modalCardBody}>
                    {children}
                </div>
            </div>
        </div>
    );
}

// Поле формы с лейблом
function Field({ label, children }) {
    return (
        <div className={cl.modalField}>
            <label className={cl.modalLabel}>{label}</label>
            {children}
        </div>
    );
}

export default EditEvent;
